import ReactDOM from "react-dom";
import BaseComponent from "./BaseComponent";
import InputSuggestion from "./InputSuggestion";
import { Constant, speakAsOptions } from '../const';
import Utility from "../utilities/Utility";
import { Label } from "./label";

class Input extends BaseComponent {
    /**
     * Input component; includes input box and optional validation & suggestion
     * @module Input
     * @param {Object} props React props
     */
    constructor(props) {
        super(props);
        this.initialValue = this.props.value;
        this.state = {
            isPassword: this.props.isPassword ? true : false,
            hint: this.props.hint ? this.props.hint : "",
            suggestions: [],
            value: this.props.value
        };
        this.initSupportedRules();
        this.clickOutsideHandler = this.handleClickOutside.bind(this);
        this.suggestionInstance = null;
        this.selectionEnd = 0;
        this.selectionStart = 0;
    }

    /**
     * Define React default props:
     * allows to add placeholders or functions to props which we expect to execute
     * in code at some point. Helps to avoid adding extra validations for undefined props.
    */
    static defaultProps = {
        validateValueCallback: function(isValid, value) {
            return [isValid, value];
        }
    }

    /**
     * Get reference to input box itself
     * @returns {jQuery}
     */
    getDomSelf() {
        return $(ReactDOM.findDOMNode(this)).children("input");
    }

    

    /**
     * Get reference to the error message container
     * @returns {jQuery}
     */
    getErrorContainer() {
        return $(ReactDOM.findDOMNode(this)).children(".form-error-tip");
    }

    /**
     * Initialize supported validation rules
     * "f" is the validation function; "t" is the error message if validation fails
     * Validation function should return true if the result is VALID; return false if the result is INVALID
     */
    initSupportedRules = () => {
        // Input validation; validate during user input, against a single character
        this.supportedInputRules = {
            "numberOnly": {
                "f":  (keyCode) =>  keyCode >= 48 && keyCode <= 57,
                "t": this.getPropLabel("number_only")
            },
            "limit": {
                "f":  (_, limit) => this.getDomSelf().val().length < limit,
                "t": this.getPropLabel("reach_limit")
            },
            "alphanumeric": {
                "f": (keyCode) => {
                    return String.fromCharCode(keyCode).match(/^[0-9a-zA-Z]/);
                },
                "t": this.getPropLabel("invalid_repo_name")
            },
        };
        // Value validation; validate after user input, against current input box value
        this.supportedValRules = {
            "noEmpty": {
                "f": function (value) {
                    if (!value) {
                        return false;
                    }
                    const trimmed = value.trim();
                    return trimmed !== "";
                },
                "t": this.getPropLabel("no_empty")
            },
            "noDuplicate": {
                "f": (value) => {
                    return !window.apm.isTreeNameDuplicated($('#tm_container'), value, this.props.context.type, this.props.context.props.nodeTypeName, this.props.context.id);
                },
                "t": this.getPropLabel("duplicate_name")
            },
            "noBranchDuplicate": {
                "f": (value) => {
                    const clientApps = this.props.clientApps;
                    return !clientApps.some(app => app === value);
                },
                "t": "Duplicate name. Please pick a unique branch name"
            },
            "noDuplicateDesignName": {
                "f": (value) => {
                    return !this.props.designNames.some(design => design.name === value);
                },
                "t": "Duplicate name. Please pick a unique design name"
            },
            "noDuplicateLib": {
                "f": (value) => {
                    return !window.apm.isTreeNameDuplicated($('#tl_container'), value, this.props.context.type, this.props.context.props.nodeTypeName, this.props.context.id);
                },
                "t": this.getPropLabel("duplicate_name_app")
            },
            "noDuplicateFolder": {
                "f": (value) => {
                    return !window.apm.isFolderDuplicated(value, this.props.context.id);
                },
                "t": this.getPropLabel("duplicate_name_app")
            },
            "defaultValue": {
                "f": (value) => {
                    return !speakAsOptions.find(({defaultValue}) => defaultValue === value);
                },
                "t": "Consider creating your own default value"
            },
            "variableName": {
                "f": (value) => {
                    return value.match(/^[a-zA-Z]{1,}[0-9a-zA-Z\-\_]*$/ig) !== null;
                },
                "t": this.getPropLabel("invalid_variable_name")
            },
            "varName": {
                "f": (value) => {
                    const scope = value.split(".")[0];
                    if (scope === "param") {
                        return false;
                    }
                    else {
                        return true;
                    }

                },
                "t": this.getPropLabel("invalid_variable")
            },
            "argumentName": {
                "f": (value) => {
                    const scope = value.split(".")[0];
                    const varName = value.split(".")[1];
                    if (scope!=="mod") {
                        return false;
                    }
                    else {
                        return true;
                    }

                },
                "t": this.getPropLabel("invalid_argumentName")
            },
            "moduleReference": {
                "f": (value) => {
                    const scope = value.split(".")[0];
                    const varName = value.split(".")[1];
                    if (scope==="mod"||scope==="app") {
                        return true;
                    }
                    else {
                        return false;
                    }

                },
                "t": this.getPropLabel("invalid_moduleReference")
            },
            "propertyConfigNoDuplicate": {
                "f": (value) => {
                    value = value.toUpperCase().replace(/ /g, "_");
                    const configs = window.apm.serialized.appProperties.map(p => p.config).filter(c => c !== this.initialValue);
                    return configs.indexOf(value) === -1;
                },
                "t": "Config already exists."
            },
            "propertyVariable": {
                "f": (value) => {
                    const varComponents = value.split(".");
                    const scope = varComponents.length === 1 ? "app" : varComponents[0];
                    const varName = varComponents.length === 1 ? varComponents[0] : varComponents[1];
                    return varComponents.length <= 2 && scope === "app" && varName.match(/^[a-zA-Z]{1,}[0-9a-zA-Z\-\_]*$/ig) !== null;
                },
                "t": "Only application scoped variable accepted. E.g. app.name"
            },
            "propertyVariableNoDuplicate": {
                "f": (value) => {
                    const variables = window.apm.serialized.appProperties.map(p => p.variable).filter(v => v !== this.initialValue);
                    return variables.indexOf(value) === -1;
                },
                "t": "Variables are not allowed to be bound to more than one config."
            },
            "transitionName": {
                "f": (value, key, event) => {

                    value = value.toUpperCase().replace(/ /g, "_");
                    this.getDomSelf().val(value);
                    return true;


                },

            },
            "invalidValue": {
                "f": (value, key, event, type) => {
                    if (type === "NUMBER") {
                        return Number(value);
                    }
                    else if (type === "BOOLEAN") {
                        if (value.toLowerCase() === "true" || value.toLowerCase() === "false") {
                            return true;
                        }
                    }
                    else {
                        return true;
                    }

                },
                "t": this.getPropLabel("invalid_value")
            },
            "objectName": {
                "f": (value, key, event) => {

                    value = value.toUpperCase().replace(/ /g, "_");

                    const caretPos = this.getDomSelf()[0].selectionStart;
                    this.getDomSelf().val(value);
                    this.getDomSelf()[0].selectionStart = caretPos;
                    this.getDomSelf()[0].selectionEnd = caretPos;

                    return value === "" ? true : (value.match(/^[A-Z][A-Z0-9_]*$/) !== null);
                },
                "t": this.getPropLabel("invalid_variable_name")
            },
            "headerName": {
                "f": (value, key, event) => {

                    value = value.replace(/ /g, "-");

                    const caretPos = this.getDomSelf()[0].selectionStart;
                    this.getDomSelf().val(value);
                    this.getDomSelf()[0].selectionStart = caretPos;
                    this.getDomSelf()[0].selectionEnd = caretPos;
                    
                    return value === "" ? true : (value.match(/^[A-Z]{1,3}[a-z\-]+|^[A-Z]{1,3}/) !== null);
                },
                "t": "Invalid header name. Must begin with uppercase character."
            },
            "repoName": {
                "f": (value) => {
                    return value.match(/^[a-zA-Z]{1,1}[a-zA-Z0-9\_]{1,49}$/);
                },
                "t": this.getPropLabel("invalid_repo_name")
            },
            "branchName": {
                "f": (value) => {
                    return Utility.validateBranchName(value);
                },
                "t": this.getPropLabel("invalid_branch_name")
            },
            "noDuplicateVar": {
                "f": (value) => {
                    return !window.apm.isVariableDuplicated(value, this.props.context.props.parentModule, this.props.context.id);
                },
                "t": this.getPropLabel("duplicate_name_scope")
            },
            "noDupFunctionName":{
                "f": (value) => {
                return !window.apm.isfunctionNameDuplicated(value,this.props.context.props.new);
                },
                "t": this.getPropLabel("duplicate_reusable_function_name")
            },
            "dtmfOverride": {
                "f": (value)=> value ? value.match(/^(\d|#|\*)$/) : true,
                "t": this.getPropLabel('dtmf_live_agent_override_error')
            },
            "numberOrVariableRef": {
                "f": (value) => {
                    const isNumber = !isNaN(value);

                    // Is not a numeric value which means that they entered a string
                    // therefore we need to check if it's a variable reference.
                    if (!isNumber) {
                        return this.supportedValRules.moduleReference.f(value);
                    }

                    // Is a numeric value.
                    return true;
                },
                "t": this.getPropLabel("invalid_number_or_variable_reference"),
            },
        };
    }

    componentDidMount() {
        
        this.getDomSelf().val(this.state.value);
        if(!this.props.onBlurOnly){
            this.validateValue();
        }
        document.addEventListener('mousedown', this.clickOutsideHandler, true);
        document.addEventListener('wheel', this.clickOutsideHandler, { passive: false });

    }

    componentWillUnmount() {
        document.removeEventListener('mousedown', this.clickOutsideHandler);
        document.removeEventListener('wheel', this.clickOutsideHandler);
    }


    componentDidUpdate() {
        // If this is a readonly input we want to update the
        // field value immediately after detecting any change
        // since the user won't be typing in in the field.
        if (this.props.readonly) {
            this.getDomSelf().val(this.getContextValue());
        } else {
            // If this is not a readonly field then we want
            // to make sure to update the field value when needed.
            const inputValue = this.getDomSelf().val();
            const contextValue = this.getContextValue();

            const isUpdateRequired = inputValue?.toString() !== contextValue?.toString();

            if (isUpdateRequired) {
                this.getDomSelf().val(contextValue);
            }
        }

        if(!this.props.onBlurOnly){
            this.validateValue();
        }
    }

    /**
     * Handle mouse event happening outside the reference node
     * @param {Object} event React event
     */
    handleClickOutside(event) {
        
        if (this.wrapperRef && !this.wrapperRef.contains(event.target)) {
            const isFocused = this.getDomSelf().is(':focus');
            
            // DEVDES2-1239: updateContext when clicking outside of inputs in property panel
            if(isFocused){
                if(this.props.valRules && this.props.valRules['noEmpty']&&this.props.label==="Name"&&this.initialValue!==""&&this.getDomSelf().val()===""){
                    //if the updated value is empty we are adding the "cannot be empty" validation to the UI, removing that as we are not updating the value to empty
                    return delete this.props.context.props.error['inputValRules::name::noEmpty'];
                }
                
                else{
                    this.updateContext(this.getDomSelf().val());
                }
                   
                
               
            }
            this.hideSuggestion();
        }
    }

    /**
     * Set React reference of input box suggestion box
     * @param {Object} node React node reference
     */
    setWrapperRef(node) {
        this.wrapperRef = node;
    }

    /**
     * Carry out input validation
     * @param {number} keyCode React keyboard event keycode
     * @returns {boolean} True if input is valid; false otherwise
     */
    validateInput(keyCode) {
        if (this.props.inputRules) {
            for (var key in this.props.inputRules) {
                if (this.supportedInputRules[key]) {
                    const valid = this.supportedInputRules[key]["f"](keyCode, this.props.inputRules[key]);
                    if (!valid) {
                        this.showErrorTip(this.supportedInputRules[key]["t"], "warning");
                        return false;
                    }
                }
            }
        }
        return true;
    }

    /**
     * Carry out value validation
     * @returns {boolean} True if value is valid; false otherwise
     */
    validateValue(event) {
        const value = this.getDomSelf().val();

        if (typeof this.props.context.removeErrors === "function") {
            this.props.context.removeErrors("inputValRules::" + this.props.valKey);
        }

        if (this.props.valRules) {
            for (var key in this.props.valRules) {
                if (!this.props.valRules[key]) {
                    continue;
                }
                if (this.supportedValRules[key] && key !== "invalidValue") {
                    const valid = this.supportedValRules[key]["f"](value, this.props.valRules[key], event);
                    if (!valid) {
                        const errorType = (this.props.valKey === 'defaultValue') ? 'warning' : 'error';
                        this.showErrorTip(this.supportedValRules[key]["t"], errorType);
                        if (typeof this.props.context.setError === "function") {
                            this.props.context.setError("inputValRules::" + this.props.valKey + "::" + key, this.supportedValRules[key]["t"]);
                        }

                        this.props.validateValueCallback(false, value);
                        return false;
                    } else {
                        if (typeof this.props.context.removeError === "function") {
                            this.props.context.removeError("inputValRules::" + this.props.valKey + "::" + key);
                        }
                    }
                }
                else if (this.supportedValRules[key] && key === "invalidValue" && this.props.valKey === "value") {
                    const valid = this.supportedValRules[key]["f"](value, this.props.valRules[key], event, this.props.context.varModel.type);
                    if (!valid) {
                        this.showErrorTip(this.supportedValRules[key]["t"]);
                        if (typeof this.props.context.setError === "function") {
                            this.props.context.setError("inputValRules::" + this.props.valKey + "::" + key, this.supportedValRules[key]["t"]);
                        }

                        this.props.validateValueCallback(false, value);
                        return false;
                    }
                }
            }
        }

        this.props.validateValueCallback(true, value);
        return true;
    }

    /**
     * Get the snippet for use with suggestion
     * @returns {null|Array}
     */
    getSnippet() {
        var snippet = null;
        const cPos = this.getDomSelf().getCursorPosition();
        if (cPos > 0) {
            const clip = this.getDomSelf().val().substring(0, cPos);
            // Text between the last whitespace and the current cursor position
            snippet = clip.match(/([0-9a-zA-Z_\-]+)(\.[0-9a-zA-Z_\-]*)?$/i);
        }
        return snippet;
    }

    getValueSnippet() {
        var snippet = null;
        const cPos = this.getDomSelf().getCursorPosition();
        if (cPos > 0) {
            const clip = this.getDomSelf().val().substring(0, cPos);
            // Text between out.* [comparison operator] [possible value]
            // /(out)(\.[0-9a-zA-Z_\-]+)?\s*(==|!=|===|!==|>|<|>=|<=)\s*(["']?[0-9a-zA-Z_\-]*["']?)$/i
            // (g1 out) (g2 .*) (g3 comparison operator) (g4 possible value)
            const regex = new RegExp("(" + Constant.k.SCOPE_OUTPUT + ")(\\.[0-9a-zA-Z_\\-]+)?\\s*(==|!=|===|!==|>|<|>=|<=)\\s*([\"']?[0-9a-zA-Z_\\-]*[\"']?)$", "i");
            snippet = clip.match(regex);
        }
        return snippet;
    }

    /**
     * Show suggestion box
     * @param {null|string|Array} snippet Snippet of user input that triggers the suggestion
     * @param {Object} suggestions List of suggestion items
     * @param {string|null} suggestionType
     */
    showSuggestion(snippet, suggestions, suggestionType = null) {
        this.hideSuggestion();
        const s = this.getDomSelf().parent().find(".suggestion");
        var hasSuggestions = false;
        for (var key in suggestions) {
            if (suggestions[key].length) {
                hasSuggestions = true;
                break;
            }
        }
        if (s.length && hasSuggestions) {
            s.show();
            if (suggestionType === null) {
                suggestionType = this.props.suggestion;
            }
            this.suggestionInstance = ReactDOM.render(<InputSuggestion snippet={snippet} suggestions={suggestions}
                context={this} type={suggestionType}
                inUse={this.props.inUse} excludeInUse={this.props.excludeInUse} />, s.get(0));
            this.isSuggestionShown = true;
        }
    }

    /**
     * Hide suggestion box
     */
    hideSuggestion() {
        if (this.suggestionInstance) {
            this.suggestionInstance.unregister();
        }
        const s = this.getDomSelf().parent().find(".suggestion");
        if (s) {
            s.hide();
            this.isSuggestionShown = false;
        }
    }

    /**
     * Apply the user-selected item from suggestion box back to input box
     * @param {string|null} suggestion The user-selected suggestion value
     */
    applySuggestion(suggestion, suggestionType = null) {
        
        if (suggestionType === null) {
            suggestionType = this.props.suggestion;
        }
        if(suggestionType === "prompt"){
            this.getDomSelf().val(suggestion);
            this.updateContext(this.getDomSelf().val());
            this.getDomSelf().trigger("focus");
        }
        if (suggestionType === "variable") {
            const snippet = this.getSnippet();
            if (snippet) {
                var length = 0;
                if (!snippet[2]) {
                    length = snippet[1].length;
                } else {
                    length = snippet[2].length + snippet[1].length;
                }
                const end = this.getDomSelf().getCursorPosition();
                const start = end - length;
                const currentVal = this.getDomSelf().val();

                // Replace the snippet entirely with the suggestion value
                this.getDomSelf().val(currentVal.substring(0, start) + suggestion + currentVal.substring(end));
                this.validateValue();
                this.updateContext(this.getDomSelf().val());
                this.getDomSelf().trigger("focus");
            }
        }
        this.hideSuggestion();
        this.updateContext(suggestion);
    }

    /**
     * Show error message
     * @param {string} tip Error message
     * @param {string} type Message type
     */
    showErrorTip(tip, type = "error") {
        this.getErrorContainer().addClass(type).html(tip).show();
        this.getDomSelf().addClass(type).animateCss?.("shake");
    }

    /**
     * Hide error message
     */
    hideErrorTip() {
        this.getDomSelf().removeClass("error").removeClass("warning");
        this.getErrorContainer().removeClass("error").removeClass("warning").html("&nbsp;").hide();
        if(this.props.client){
            $("#validation_message").html("");
        }
    }

    /**
     * React render
     * @returns {XML}
     */
    render() {
        // Note: Input component renders on Front Page where `window.apm` isn't yet defined 
        let sortedIntents = [];
        let uniqueIntents = [];
        if(window.apm){
            sortedIntents = JSON.parse(JSON.stringify(window.apm.knowledgeBase.getIntents())).sort((a, b) => a.name.localeCompare(b.name));
            uniqueIntents = sortedIntents.filter((v,i,a)=>a.findIndex(t=>(t.name === v.name))===i);
        } 
        const isHidden = this.props?.isHidden || false;
        
        return (
            <div className={"form-component-container"}
                style={{display: isHidden ? "none" : "block"}}
                ref={this.setWrapperRef.bind(this)}
            >
                {(!this.props.label || this.props.inputType) ? null : (
                    <Label tooltip={this.props.tooltip}>{this.props.label}</Label>
                )}

                <input
                    type={this.state.isPassword ? "password" : "text"}
                    spellCheck={false}
                    disabled={this.props.hasOwnProperty("disabled") && this.props.disabled}
                    readOnly={this.props.hasOwnProperty("readonly") && this.props.readonly}
                    placeholder={this.state.hint}
                    className="form-component"
                    onFocus={event => {
                        // this.setConfig('hasFocus', true);
                        this.getDomSelf().append('<style>.submenu-overlay:before{top: ' + 'px !important;}</style>');
                        if (this.props.suggestion === "value" && this.props.suggestions && this.props.suggestions.length) {
                            this.showSuggestion("", { "value": this.props.suggestions });
                        }
                        if (this.props.suggestion === "intent") {
                            this.showSuggestion("", { "intent": sortedIntents });
                        }
                        if (this.props.suggestion === "prompt") {
                            const prompts  = JSON.parse(JSON.stringify(window.apm.knowledgeBase.getPrompts()));
                            this.showSuggestion("", { "prompt": prompts });
                        }
                    }}
                    onChange={() => {
                        if (this.props.valReplace) {
                            const { searchValue, replaceValue } = this.props.valReplace;
                            const searchValueRegExp = new RegExp(searchValue, "g");
                            const currentValue = this.getDomSelf().val();
                            this.getDomSelf().val(currentValue.replace(searchValueRegExp, replaceValue));
                        }
                    }}
                    onInput={event => {
                        if(!this.props.onBlurOnly){
                            this.validateValue();
                        }

                        if (this.props.suggestion === "value" && this.props.suggestions && this.props.suggestions.length) {
                            const snippet = this.getDomSelf().val();
                            if (snippet) {
                                const query = new RegExp(snippet, "ig");
                                const suggestion = { "value": [] };
                                for (let i = 0; i < this.props.suggestions.length; i++) {
                                    const s = this.props.suggestions[i];
                                    if (s.match(query)) {
                                        suggestion["value"].push(s);
                                    }
                                }
                                this.showSuggestion(snippet, suggestion, "value");
                            }
                        }

                        if (this.props.suggestion === "variable") {
                            const valueSnippet = this.getValueSnippet();
                            if (valueSnippet) {
                                if (this.props.localSuggestion && this.props.localSuggestion.value && this.props.localSuggestion.value.length) {
                                    if (valueSnippet) {

                                        if (!valueSnippet[4]) {
                                            this.showSuggestion(valueSnippet, this.props.localSuggestion.value, "value");
                                        } else {
                                            const query = new RegExp(valueSnippet[4].replace(/['"]/i, ""), "i");
                                            var suggestion = [];

                                            for (var i = 0; i < this.props.localSuggestion.value.length; i++) {
                                                const target = JSON.stringify(this.props.localSuggestion.value[i]);
                                                if (target.match(query)) {
                                                    suggestion.push(this.props.localSuggestion.value[i]);
                                                }
                                            }
                                            this.showSuggestion(valueSnippet, suggestion, "value");
                                        }
                                    }
                                }
                            } else {
                                const variableSnippet = this.getSnippet();
                                if (variableSnippet) {
                                    var suggestion = {};
                                    if (!variableSnippet[2]) {
                                        if (this.props.label === "Module Reference")suggestion = window.apm.knowledgeBase.getVariableSuggestions(variableSnippet[1], null, "moduleReference");
                                        else if (this.props.label === "Prompt Reference") suggestion = window.apm.knowledgeBase.getVariableSuggestions(variableSnippet[1], null, "promptReference");
                                        else if (this.props.keyType && this.props.keyType === "conversationVariable") suggestion = window.apm.knowledgeBase.getVariableSuggestions(variableSnippet[1], null, "conversationVariable");
                                        else suggestion = window.apm.knowledgeBase.getVariableSuggestions(variableSnippet[1]);
                                    } else {
                                        if (this.props.label === "Module Reference") suggestion = window.apm.knowledgeBase.getVariableSuggestions(variableSnippet[2].substring(1), variableSnippet[1], "moduleReference");
                                        else if (this.props.label === "Prompt Reference") suggestion = window.apm.knowledgeBase.getVariableSuggestions(variableSnippet[2].substring(1), variableSnippet[1], "promptReference");
                                        else suggestion = window.apm.knowledgeBase.getVariableSuggestions(variableSnippet[2].substring(1), variableSnippet[1]);
                                    }
                                    if (this.props.localSuggestion && this.props.localSuggestion.out && this.props.localSuggestion.out.length) {
                                        suggestion = Object.assign(suggestion, this.props.localSuggestion.out);
                                    }
                                    if(this.props.excludeSensitiveVariables) {
                                        Object.keys(suggestion).forEach(varType => {
                                            suggestion[varType] = suggestion[varType].filter(variable => !variable.props.config.sensitiveData);
                                        });
                                    }
                                    this.showSuggestion(variableSnippet, suggestion);
                                }

                            }
                        }
                        else if (this.props.suggestion === "intent") {
                            const snippet = this.getDomSelf().val();
                            if (snippet) {
                                const query = new RegExp(snippet, "ig");
                                var suggestion = { "intent": [] };
                                for (var i = 0; i < sortedIntents.length; i++) {
                                    if (sortedIntents[i].name.match(query)) {
                                        suggestion["intent"].push(sortedIntents[i]);
                                    }
                                }

                                this.showSuggestion(snippet, suggestion);
                            }
                        }
                        else if (this.props.suggestion === "prompt") {
                            const snippet = this.getDomSelf().val();
                            if (snippet) {
                                const prompts = JSON.parse(JSON.stringify(window.apm.knowledgeBase.getPrompts()));
                                const query = new RegExp(snippet, "ig");
                                const suggestion = { "prompt": [] };
                                for (let i = 0; i < prompts.length; i++) {
                                    if (prompts[i].name.match(query)) {
                                        suggestion["prompt"].push(prompts[i]);
                                    }
                                }

                                this.showSuggestion(snippet, suggestion);
                            }
                        }
                        else if (this.props.suggestion === "module") {
                            const snippet = this.getDomSelf().val();
                            if (snippet) {
                                const modules = JSON.parse(JSON.stringify(window.apm.knowledgeBase.getModules()));
                                const query = new RegExp(snippet, "ig");
                                var suggestion = { "module": [] };
                                for (var i = 0; i < modules.length; i++) {
                                    if (modules[i].name.match(query)) {
                                        suggestion["module"].push(modules[i].name);
                                    }
                                }

                                this.showSuggestion(snippet, suggestion);
                            }
                        }
                        else if (this.props.suggestion === "session") {
                            const snippet = this.getDomSelf().val();
                            
                            if (snippet) {
                                const sessions = JSON.parse(JSON.stringify(this.props.suggestionList));
                                const query = new RegExp(snippet, "ig");
                                var suggestion = { "session": [] };
                                for (var i = 0; i < sessions.length; i++) {
                                    if (sessions[i].name.match(query)) {
                                        let sessionId = sessions[i].value
                                        suggestion["session"].push(sessionId);
                                    }
                                }

                                this.showSuggestion(snippet, suggestion);
                            }
                        }

                        if (this.state.isPassword) {
                            return this.updateContext(this.getDomSelf().val().trim());
                        }
                    }}
                    onKeyPress={event => {
                        this.selectionStart = event.target.selectionStart;
                        this.selectionEnd = event.target.selectionEnd;
                        if (!this.validateInput(event.keyCode === 0 ? event.charCode : event.keyCode)) {
                            event.preventDefault();
                        }
                    }}
                    onBlur={event => {
                        // skipOnBlur
                        if(this.props.skipOnBlur === "startSession"){
                            return false;
                        }

                        const value = this.getDomSelf().val().trim();

                        if(this.props.onBlurOnly){
                            this.validateValue(event);
                        }

                        const valKey = this.props.valKey;
                        const isPromptNode = this.props.context && this.props.context.props && this.props.context.props.nodeTypeName === 'Prompt';

                        // Callback used to controll a dropdown key paired with this Input. See KeyValue component 
                        if(this.props.context.resetSelectedKeyOption){
                            this.props.context.resetSelectedKeyOption(value);
                        }

                        // return if value has not changed
                        if(this.props.value === value){
                            return
                        }
                        //return if the updated value is empty
                        if( this.props.valRules && this.props.valRules['noEmpty']&&this.props.label==="Name"&&this.initialValue!==""&&value===""){
                            //if the updated value is empty we are adding the "cannot be empty" validation to the UI, removing that as we are not updating the value to empty
                            return delete this.props.context.props.error['inputValRules::name::noEmpty'];
                         }
                        // from FillerPrompts
                        if (valKey === "externalAudioServiceUrl" && !isPromptNode) {
                            const { type, idx } = this.props;
                            return this.updateContext({ value, type, idx });
                        }
                        
                        else {
                            return this.updateContext(value);
                        }
                    }}
                    onKeyUp={event => {
                        if (this.state.isPassword) {
                            if (event.getModifierState && event.getModifierState('CapsLock')) {
                                this.showErrorTip("Caps lock is on", "warning");
                            } else {
                                this.hideErrorTip();
                            }
                        }
                    }}
                    onKeyDown={event => {
                        if (this.state.isPassword) {
                            if (event.getModifierState && event.getModifierState('CapsLock')) {
                                this.showErrorTip("Caps lock is on", "warning");
                                return;
                            }
                        }
                        this.hideErrorTip();
                        switch (event.keyCode) {
                            case 13: // enter
                                if (this.isSuggestionShown) {
                                    event.preventDefault();
                                    event.stopPropagation();
                                    const canvasEv = new CustomEvent('ev-input-nav-key', { detail: "enter" });
                                    document.dispatchEvent(canvasEv);
                                } else {
                                    event.preventDefault();
                                    this.getDomSelf().trigger("blur");
                                    const canvasEv = new CustomEvent('ev-input-key-down', { detail: "enter" });
                                    document.dispatchEvent(canvasEv);
                                }
                                break;
                            case 27: // esc
                                this.hideSuggestion();
                                break;
                            case 38: // up
                                if (this.isSuggestionShown) {
                                    event.preventDefault();
                                    event.stopPropagation();
                                    const canvasEv = new CustomEvent('ev-input-nav-key', { detail: "up" });
                                    document.dispatchEvent(canvasEv);
                                }
                                break;
                            case 40: // down
                                if (this.isSuggestionShown) {
                                    event.preventDefault();
                                    event.stopPropagation();
                                    const canvasEv = new CustomEvent('ev-input-nav-key', { detail: "down" });
                                    document.dispatchEvent(canvasEv);
                                }
                                break;
                            case 8:
                            case 46:
                                if (event.target.selectionEnd - event.target.selectionStart > 0) {
                                    this.selectionStart = event.target.selectionStart - 1;
                                    this.selectionEnd = event.target.selectionStart - 1;
                                }
                                else {
                                    this.selectionStart = event.target.selectionStart;
                                    this.selectionEnd = event.target.selectionEnd - 2;
                                }
                                break;
                        }
                    }}
                />
                {this.props.suggestion && (
                    <div style={{ position: "relative" }}>
                        <div className="suggestion form-list">
                        </div>
                    </div>
                )}
                <div className="form-error-tip">&nbsp;</div>
            </div>

        );
    }
}

export default Input;